/**
 * Logical app-history stack for native shells (iOS WKWebView + Android WebView).
 *
 * Why this exists
 * ---------------
 * WKWebView's `allowsBackForwardNavigationGestures` is bound to the native
 * `WKBackForwardList`, which on a long-running SPA inside Capacitor has
 * documented sync bugs with `history.pushState/replaceState/go`:
 *   - `pushState` does not always evict forward entries from
 *     `WKBackForwardList`.
 *   - `replaceState` is invisible to `WKBackForwardList`.
 *   - Snapshots and entries can persist across app foreground/background.
 *   - `history.go(-N)` may fire delayed or multiple popstates.
 *
 * The result is that swipe-back can land on stale entries (Home, a previous
 * category, or a different session entirely) — exactly the symptoms
 * reported on TestFlight.
 *
 * The fix
 * -------
 * We maintain our own logical history stack in `sessionStorage`, mirroring
 * every wouter `pushState` / `replaceState` / browser `popstate` via the
 * single set of global listeners installed by `startAppHistoryListener()`.
 * `logicalBack()` then navigates to whichever URL our stack reports as the
 * previous logical screen — completely independent of `WKBackForwardList`.
 *
 * The native iOS edge-swipe handler (see `iosEdgeSwipe.ts`) and the
 * Capacitor `backButton` listener (Android hardware back) both invoke
 * `appBack()`, so every back surface — gesture, hardware key, visible back
 * button — pops the same logical stack.
 *
 * Web behaviour
 * -------------
 * The stack is maintained on web too (sessionStorage is universally
 * available), but `appBack()` falls back to `window.history.back()` so the
 * browser's native back/forward arrows continue to work as users expect.
 * The stack is purely additive on web.
 *
 * Scope (deliberately small)
 * --------------------------
 * - One sessionStorage key.
 * - One module-level listener install (idempotent).
 * - No coupling to wouter's exports beyond standard window events
 *   wouter already monkey-patches.
 */

import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import {
  getCategoryTrail,
  setCategoryRestore,
  clearCategoryTrail,
} from "./categoryTrail";
import { capturePageSnapshot, dropPageSnapshot } from "./pageSnapshot";

const STACK_KEY = "iraqsooq.appHistory.stack.v1";
const MAX_STACK = 50; // hard cap so a buggy loop can never grow unbounded

function safeSessionStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readStack(): string[] {
  const ss = safeSessionStorage();
  if (!ss) return [];
  try {
    const raw = ss.getItem(STACK_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === "string");
  } catch {
    return [];
  }
}

function writeStack(stack: string[]): void {
  const ss = safeSessionStorage();
  if (!ss) return;
  try {
    const trimmed =
      stack.length > MAX_STACK ? stack.slice(stack.length - MAX_STACK) : stack;
    ss.setItem(STACK_KEY, JSON.stringify(trimmed));
  } catch {
    /* private mode / quota — ignore */
  }
}

function currentUrl(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

/**
 * Initialize the stack on app boot. If sessionStorage carries a stack from
 * a previous foreground session whose top doesn't match the URL we are
 * actually rendering, we reset it — never inherit a misaligned stack.
 */
export function initAppHistory(): void {
  if (typeof window === "undefined") return;
  const cur = currentUrl();
  const stack = readStack();
  if (stack.length === 0 || stack[stack.length - 1] !== cur) {
    writeStack([cur]);
  }
}

/**
 * Wire up the global listeners. Wouter v3 monkey-patches `pushState` and
 * `replaceState` to dispatch synthetic `pushState` / `replaceState` events
 * (see node_modules/wouter/esm/use-browser-location.js). We use those
 * events plus the native `popstate` to keep our stack in sync without
 * coupling to wouter internals.
 *
 * Idempotent: calling twice does not double-install. Wouter installs its
 * patch under a `Symbol.for("wouter_v3")` flag; we install our listeners
 * under our own flag.
 */
const LISTENER_INSTALLED = Symbol.for("iraqsooq.appHistory.listenerInstalled");
export function startAppHistoryListener(): void {
  if (typeof window === "undefined") return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w[LISTENER_INSTALLED]) return;
  w[LISTENER_INSTALLED] = true;

  window.addEventListener("pushState", () => {
    const cur = currentUrl();
    const stack = readStack();
    if (stack.length > 0 && stack[stack.length - 1] === cur) return;
    // Snapshot the page we are LEAVING (still rendered in the DOM at this
    // point — wouter's pushState event fires synchronously before React
    // re-renders) so the iOS edge-swipe can later show it underneath the
    // live shell as the user drags the current screen out of view.
    const leaving = stack.length > 0 ? stack[stack.length - 1] : null;
    if (leaving) capturePageSnapshot(leaving);
    stack.push(cur);
    writeStack(stack);
  });

  window.addEventListener("replaceState", () => {
    const cur = currentUrl();
    const stack = readStack();
    if (stack.length === 0) {
      writeStack([cur]);
      return;
    }
    stack[stack.length - 1] = cur;
    writeStack(stack);
  });

  window.addEventListener("popstate", () => {
    const cur = currentUrl();
    const stack = readStack();
    // Trim the stack so its top equals the destination of the popstate.
    // - If `cur` already exists in the stack at index N, drop everything
    //   after N (normal back / multi-step back).
    // - Otherwise the user landed somewhere we did not record (a true
    //   browser-driven popstate to a stale entry, or first popstate after
    //   storage reset). Replace top so we converge.
    const idx = stack.lastIndexOf(cur);
    if (idx >= 0) {
      writeStack(stack.slice(0, idx + 1));
    } else if (stack.length === 0) {
      writeStack([cur]);
    } else {
      stack[stack.length - 1] = cur;
      writeStack(stack);
    }
  });
}

/**
 * Replace the entire stack with a single root entry. Used by
 * `nativeTabNavigate` for top-level tab switches (Home / Categories /
 * Messages / Account / Header home logo). Tab switches must NOT inherit
 * the deep navigation context of the previous tab, otherwise swipe-back
 * after a tab switch lands on screens from the prior tab — which is
 * exactly the symptom users reported on TestFlight.
 */
export function appHistoryReset(href?: string): void {
  const url = href ?? currentUrl();
  writeStack([url]);
}

/** Read the previous logical entry (the destination of `back`). */
export function appHistoryPrevious(): string | null {
  const stack = readStack();
  if (stack.length < 2) return null;
  return stack[stack.length - 2] ?? null;
}

/** Number of entries in the logical stack — for tests / diagnostics. */
export function appHistoryDepth(): number {
  return readStack().length;
}

/**
 * If the user is currently on `/category/<key>` AND a matching drill
 * trail exists in sessionStorage, convert the trail into a one-shot
 * "restore on next mount" marker so Home / MobileCategories re-opens the
 * drill overlay at the exact branch the user was on. Mirrors the in-page
 * "back to drill" button's behaviour but works for swipe-back / hardware
 * back too.
 *
 * Called from `appBack()` before `logicalBack()`, so it fires on every
 * back path. Best-effort: any failure is swallowed.
 */
function tryArmDrillRestoreOnLeave(): void {
  try {
    if (typeof window === "undefined") return;
    const m = window.location.pathname.match(/^\/category\/([^/]+)\/?$/);
    const rawKey = m?.[1];
    if (!rawKey) return;
    let urlKey: string;
    try {
      urlKey = decodeURIComponent(rawKey);
    } catch {
      urlKey = rawKey;
    }
    const trail = getCategoryTrail();
    if (!trail) return;
    if (trail.categoryKey !== urlKey) return;
    setCategoryRestore(trail);
    clearCategoryTrail();
  } catch {
    /* never block back navigation */
  }
}

/**
 * Pop the logical stack and navigate to the previous URL.
 * Returns `true` if a back navigation happened, `false` if already at the
 * logical root (caller decides what to do — typically exit on Android, no-op
 * on iOS edge-swipe, fall back to "/" on a visible back button).
 *
 * Why `replaceState` and not `history.back()`:
 *   `history.back()` traverses `WKBackForwardList`, which is the source of
 *   the original bug (it can land on stale entries). `replaceState` to the
 *   logical-previous URL is deterministic — wouter's monkey-patched
 *   `replaceState` re-dispatches a `replaceState` event so the SPA
 *   re-renders, and we manually fire a `popstate` so any subscribers that
 *   only listen to popstate (legacy code) are also notified.
 */
export function logicalBack(): boolean {
  if (typeof window === "undefined") return false;
  const stack = readStack();
  if (stack.length < 2) return false;
  stack.pop();
  writeStack(stack);
  const target = stack[stack.length - 1];
  if (!target) return false;
  // The snapshot for `target` (if any) was consumed by the swipe-back
  // animation that drove us here — drop it so the next visit to that URL
  // re-captures fresh content.
  dropPageSnapshot(target);
  window.history.replaceState(null, "", target);
  window.dispatchEvent(new PopStateEvent("popstate", { state: null }));
  return true;
}

/**
 * The single back entry-point used by:
 *   - the iOS edge-swipe handler
 *   - the Capacitor `backButton` (Android hardware back)
 *   - visible back buttons (Header, ListingDetail, Messages …)
 *
 * Order:
 *   1. Let the mobile category drill overlay consume the press if it has
 *      its own internal levels to pop. Same contract as `main.tsx` had
 *      before — we just centralize the call site.
 *   2. On native: `logicalBack()`. If the logical stack is exhausted and
 *      we are on Android, exit the app (matching the previous behaviour
 *      of the Capacitor `backButton` listener).
 *   3. On web: defer to the browser's `history.back()` so users keep their
 *      native browser back/forward UX.
 */
export function appBack(): boolean {
  if (typeof window === "undefined") return false;

  // 1. Drill overlay first
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const drillBack = (window as any).__iraqsooqDrillBack as
      | (() => boolean)
      | undefined;
    if (typeof drillBack === "function") {
      if (drillBack() === true) return true;
    }
  } catch {
    /* ignore drill consume errors and fall through */
  }

  // 2. Native: arm drill-restore (no-op unless on /category/<key>) then
  //    pop the logical stack.
  if (Capacitor.isNativePlatform()) {
    tryArmDrillRestoreOnLeave();
    if (logicalBack()) return true;
    // Logical root: on Android, exit; on iOS, no-op (matches iOS Safari
    // behaviour where edge-swipe at root does nothing).
    if (Capacitor.getPlatform() === "android") {
      try {
        CapacitorApp.exitApp();
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  // 3. Web: browser-native back
  if (window.history.length > 1) {
    window.history.back();
    return true;
  }
  return false;
}
